import React, {useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import ModuleContainer from '../../components/module-container'
import {Box, Flex, useSlotRecipe, Table} from '@chakra-ui/react3'
import Heading from '../../components/cms/heading'
import {comparisonTableRecipe} from './index.recipe'
import Asset from '../../components/cms/asset'
import Icon from '../../components/cms/icon'
import {Text} from '@chakra-ui/react'
import {hasValidPrice} from '../../utils/utils'
import Image from '../../components/cms/image'
import CTA from '../../components/cms/cta'
import PriceButtonGroup from '../../components/cms/price-button-group'
import {useIntl} from 'react-intl'
import AspectRatio from '../../components/cms/aspect-ratio'
import DropdownComponent from '../../components/dropdown'

/**
 * Comparison Table module component that displays a product comparison table with up to 4 products
 * side by side, allowing users to compare attributes, prices, colors, and specifications.
 *
 * The component uses CSS Grid to automatically align row heights across all columns, ensuring
 * a consistent visual layout. Each column has a dropdown selector to change which product is displayed.
 * The table is responsive, showing 2 columns on mobile and 4 columns on desktop (s breakpoint and above).
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} [props.moduleId] - Unique identifier for the module
 * @param {string} [props.moduleType] - Type of the module
 * @param {string} [props.pageId] - ID of the page containing this module
 * @param {number} [props.headingLevel] - Heading level for accessibility (1-6)
 * @param {Object} [props.heading] - Heading configuration object
 * @param {string} [props.heading.subline] - Optional subline text above the headline
 * @param {string} [props.heading.headline1] - Primary headline text
 * @param {string} [props.heading.headline2] - Secondary headline text
 * @param {boolean} [props.heading.separateLines] - Whether to display headlines on separate lines
 * @param {boolean} [props.heading.dualTone] - Whether to apply dual-tone styling
 * @param {string} [props.heading.variant] - Heading variant style
 * @param {string} [props.heading.headlineSize] - Size variant for the headline
 * @param {string} [props.anchor] - Anchor ID for deep linking
 * @param {'default'|'subtle'|'strong'|'inverse'} [props.background] - Background variant
 * @param {string} [props.spacing] - Spacing variant of the module
 * @param {Array<Object>} props.products - Array of products to compare (up to 4 displayed)
 * @param {string} props.products[].name - Product name shown in the dropdown selector
 * @param {Object} props.products[].image - Product image asset
 * @param {Object} [props.products[].comparisonProduct] - Product comparison data container
 * @param {string|number} [props.products[].comparisonProduct.productId] - Product identifier
 * @param {Object} [props.products[].comparisonProduct.uspProduct] - Unique selling proposition
 * @param {string} [props.products[].comparisonProduct.uspProduct.masterProductId] - Master product ID for USP
 * @param {string} [props.products[].comparisonProduct.uspProduct.value] - USP text content
 * @param {Object} [props.products[].comparisonProduct.colorProduct] - Color options for the product
 * @param {string} [props.products[].comparisonProduct.colorProduct.masterProductId] - Master product ID for colors
 * @param {Array<Object>} [props.products[].comparisonProduct.colorProduct.colors] - Array of color variants (max 5 displayed, shows +N for more)
 * @param {string} props.products[].comparisonProduct.colorProduct.colors[].colorCode - Color code identifier
 * @param {string} props.products[].comparisonProduct.colorProduct.colors[].swatch - URL to color swatch image
 * @param {string} props.products[].comparisonProduct.colorProduct.colors[].name - Color name
 * @param {string} props.products[].comparisonProduct.colorProduct.colors[].alt - Alt text for color swatch
 * @param {Object} [props.products[].comparisonProduct.priceProduct] - Product pricing information
 * @param {string} [props.products[].comparisonProduct.priceProduct.productId] - Product ID for pricing
 * @param {string|number} [props.products[].comparisonProduct.priceProduct.value] - Price value
 * @param {string} [props.products[].comparisonProduct.priceProduct.currency] - Currency code (e.g., 'EUR', 'USD')
 * @param {string} [props.products[].comparisonProduct.priceProduct.label] - Price label (e.g., 'From')
 * @param {Array<Object>} [props.products[].attributes] - Array of product attributes to compare
 * @param {string} [props.products[].attributes[].headline] - Attribute headline/title
 * @param {string} [props.products[].attributes[].text1] - Primary attribute description text
 * @param {string} [props.products[].attributes[].text2] - Secondary attribute description text
 * @param {Object} [props.products[].attributes[].icon] - Attributes icon (e.g., Check, Cancel)
 * @param {Object} [props.products[].primaryCta] - Primary call-to-action configuration
 * @param {string} [props.products[].primaryCta.label] - CTA button label
 * @param {Object} [props.products[].primaryCta.action] - CTA action configuration
 * @param {Object} [props.products[].secondaryCta] - Secondary call-to-action configuration
 * @param {string} [props.products[].secondaryCta.label] - Secondary CTA button label
 * @param {Object} [props.products[].secondaryCta.action] - Secondary CTA action configuration
 * @param {React.Ref} ref - Forwarded ref to the module container
 * @returns {React.ReactElement} The rendered ComparisonTable component
 *
 * @example
 * <ComparisonTable
 *   moduleId="comparison-1"
 *   heading={{headline1: "Compare to find your perfect chair"}}
 *   products={[
 *     {
 *       name: "Tripp Trapp®",
 *       image: {src: "...", alt: "..."},
 *       comparisonProduct: {
 *         productId: 1001,
 *         uspProduct: {value: "The chair that grows with your child"},
 *         colorProduct: {
 *           masterProductId: "1001",
 *           colors: [{colorCode: "100", swatch: "...", name: "Peppermint", alt: "..."}]
 *         },
 *         priceProduct: {productId: "1001", value: 229, currency: "EUR"}
 *       },
 *       attributes: [
 *         {headline: "Age", text1: "Newborn - adult", icon: {name: "Check"}},
 *         {headline: "Travel Friendly", icon: {name: "Cancel"}}
 *       ],
 *       primaryCta: {label: "Shop now", action: {type: "product"}},
 *       secondaryCta: {label: "Learn more", action: {type: "content"}}
 *     }
 *   ]}
 * />
 */
const ComparisonTable = React.forwardRef(function ComparisonTable(
    {moduleId, moduleType, pageId, headingLevel, spacing, anchor, background, heading, products},
    ref
) {
    const intl = useIntl()
    const recipe = useSlotRecipe({recipe: comparisonTableRecipe})
    const dropdownItems = useMemo(
        () =>
            products?.map((item, index) => ({
                label: item.name,
                value: String(index)
            })),
        [products]
    )

    // Initialize state: each column displays the item at its index
    const [selectedItems, setSelectedItems] = useState(
        products?.slice(0, 4).map((_, index) => index) || []
    )

    const styles = useMemo(() => recipe(), [recipe])

    // Find maximum number of elements across all selected products
    const maxElements = useMemo(
        () =>
            products && selectedItems.length > 0
                ? Math.max(
                      ...selectedItems.map(
                          (itemIndex) => products[itemIndex]?.attributes?.length || 0
                      )
                  )
                : 0,
        [selectedItems, products]
    )

    const handleItemChange = (columnIndex, newItemIndex) => {
        setSelectedItems((prev) => {
            const updated = [...prev]
            updated[columnIndex] = parseInt(newItemIndex, 10)
            return updated
        })
    }

    return (
        <ModuleContainer
            moduleId={moduleId}
            moduleType={moduleType}
            pageId={pageId}
            anchor={anchor}
            background={background}
            spacing={spacing}
            heading={heading}
            ref={ref}
            {...styles.moduleContainer}
            role="region"
        >
            <Box {...styles.heading}>
                <Heading
                    alignment="center"
                    {...heading}
                    headingLevel={headingLevel}
                    {...styles.textBoxElement}
                ></Heading>
            </Box>

            <Table.Root
                variant="simple"
                tableLayout="fixed"
                aria-label={intl.formatMessage({
                    defaultMessage: 'Products',
                    id: 'leap.comparisontable.assistive_msg.products'
                })}
                {...styles.table}
            >
                <Table.Header>
                    <Table.Row>
                        {selectedItems.map((itemIndex, columnIndex) => (
                            <Table.ColumnHeader
                                key={`header-${columnIndex}`}
                                data-testid={`column-${columnIndex}-header`}
                                display={
                                    columnIndex >= 2
                                        ? {base: 'none', s: 'table-cell'}
                                        : 'table-cell'
                                }
                            >
                                <Text className="sr-only" as={`h${headingLevel + 1}`}>
                                    {products[itemIndex].name}
                                </Text>
                            </Table.ColumnHeader>
                        ))}
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {/*Select*/}
                    <Table.Row>
                        {selectedItems.map((itemIndex, columnIndex) => {
                            return (
                                <Table.Cell
                                    key={`select-${columnIndex}`}
                                    data-testid={`column-${columnIndex}-select`}
                                    {...styles.select}
                                    display={
                                        columnIndex >= 2
                                            ? {base: 'none', s: 'table-cell'}
                                            : 'table-cell'
                                    }
                                >
                                    <DropdownComponent
                                        items={dropdownItems}
                                        value={itemIndex}
                                        variant={
                                            background === 'default' || background === 'subtle'
                                                ? 'filled'
                                                : undefined
                                        }
                                        css={{
                                            '--module-bg-color': `colors.background.surface.on-section-${
                                                background !== 'inverse' ? background : 'default'
                                            }`
                                        }}
                                        onValueChange={({value: val}) => {
                                            if (val?.[0] != null) {
                                                handleItemChange(columnIndex, val[0])
                                            }
                                        }}
                                        aria-label={intl.formatMessage(
                                            {
                                                defaultMessage:
                                                    'Select product for column {columnIndex}',
                                                id: 'leap.comparisontable.assistive_msg.select-field'
                                            },
                                            {
                                                columnIndex: columnIndex + 1
                                            }
                                        )}
                                    />
                                </Table.Cell>
                            )
                        })}
                    </Table.Row>
                    {/* Row: Images */}
                    <Table.Row>
                        {selectedItems.map((itemIndex, columnIndex) => {
                            const item = products[itemIndex]
                            return (
                                <Table.Cell
                                    key={`image-${columnIndex}`}
                                    data-testid={`column-${columnIndex}-image`}
                                    {...styles.image}
                                    display={
                                        columnIndex >= 2
                                            ? {base: 'none', s: 'table-cell'}
                                            : undefined
                                    }
                                >
                                    <AspectRatio ratio="4 / 3">
                                        <Asset
                                            asset={item.image}
                                            objectFit="cover"
                                            widths={{
                                                base: '50vw',
                                                s:
                                                    products.length === 4
                                                        ? '25vw'
                                                        : products.length === 3
                                                        ? '33vw'
                                                        : '50vw'
                                            }}
                                        />
                                    </AspectRatio>
                                </Table.Cell>
                            )
                        })}
                    </Table.Row>

                    {/* Row: USP */}
                    <Table.Row>
                        {selectedItems.map((itemIndex, columnIndex) => {
                            const item = products[itemIndex]
                            return (
                                <Table.Cell
                                    key={`usp-${columnIndex}`}
                                    data-testid={`column-${columnIndex}-usp`}
                                    {...styles.element}
                                    display={
                                        columnIndex >= 2
                                            ? {base: 'none', s: 'table-cell'}
                                            : 'table-cell'
                                    }
                                >
                                    <Text alignment="center">
                                        {item.comparisonProduct?.uspProduct?.value}
                                    </Text>
                                </Table.Cell>
                            )
                        })}
                    </Table.Row>

                    {/* Row: Colors */}
                    <Table.Row>
                        {selectedItems.map((itemIndex, columnIndex) => {
                            const item = products[itemIndex]
                            return (
                                <Table.Cell
                                    key={`colors-${columnIndex}`}
                                    data-testid={`column-${columnIndex}-colors`}
                                    {...styles.element}
                                    display={
                                        columnIndex >= 2
                                            ? {base: 'none', s: 'table-cell'}
                                            : 'table-cell'
                                    }
                                >
                                    <Flex
                                        justifyContent="center"
                                        aria-label={intl.formatMessage(
                                            {
                                                defaultMessage: 'Available in {colorsCount} colors',
                                                id: 'leap.comparisontable.assistive_msg.available-colors'
                                            },
                                            {
                                                colorsCount:
                                                    item.comparisonProduct?.colorProduct?.colors
                                                        ?.length
                                            }
                                        )}
                                    >
                                        {item.comparisonProduct?.colorProduct?.colors
                                            ?.slice(0, 5)
                                            .map((color, index) => (
                                                <Box key={color + index} css={styles.swatch}>
                                                    <Image
                                                        alt={color.alt}
                                                        src={color.swatch}
                                                        cursor="inherit"
                                                        objectFit="cover"
                                                        width="100%"
                                                        height="100%"
                                                    />
                                                </Box>
                                            ))}
                                        {item.comparisonProduct?.colorProduct?.colors?.length >
                                            5 && (
                                            <Text paddingLeft="0.25rem">
                                                +{' '}
                                                {item.comparisonProduct?.colorProduct.colors
                                                    .length - 5}
                                            </Text>
                                        )}
                                    </Flex>
                                </Table.Cell>
                            )
                        })}
                    </Table.Row>

                    {/* Row: Price */}
                    <Table.Row>
                        {selectedItems.map((itemIndex, columnIndex) => {
                            const item = products[itemIndex]
                            const hasPrice = hasValidPrice(
                                item?.comparisonProduct?.priceProduct?.value
                            )
                            return (
                                <Table.Cell
                                    key={`price-${columnIndex}`}
                                    data-testid={`column-${columnIndex}-price`}
                                    {...styles.element}
                                    display={
                                        columnIndex >= 2
                                            ? {base: 'none', s: 'table-cell'}
                                            : 'table-cell'
                                    }
                                    visibility={hasPrice ? 'visible' : 'hidden'}
                                    aria-hidden={!hasPrice}
                                    {...styles.divider}
                                >
                                    {hasPrice && (
                                        <PriceButtonGroup
                                            price={{
                                                ...item.comparisonProduct?.priceProduct,
                                                label: item?.comparisonProduct?.label
                                            }}
                                            variant="comparisonTable"
                                        />
                                    )}
                                </Table.Cell>
                            )
                        })}
                    </Table.Row>

                    {/* Dynamic Attributes Rows */}
                    {Array.from({length: maxElements}).map((_, attributesIndex) => (
                        <React.Fragment key={`attributes-${attributesIndex}`}>
                            <Table.Row>
                                {selectedItems.map((itemIndex, columnIndex) => {
                                    const item = products[itemIndex]
                                    const element = item.attributes?.[attributesIndex]
                                    return (
                                        <Table.Cell
                                            key={`attributes-${attributesIndex}-${columnIndex}`}
                                            data-testid={`column-${columnIndex}-attributes${attributesIndex}`}
                                            {...styles.element}
                                            display={
                                                columnIndex >= 2
                                                    ? {base: 'none', s: 'table-cell'}
                                                    : 'table-cell'
                                            }
                                            visibility={element ? 'visible' : 'hidden'}
                                            aria-hidden={!element}
                                            {...styles.divider}
                                        >
                                            {element && (
                                                <Flex flexDirection="column" gap="spacing-8">
                                                    <Text
                                                        as={`h${headingLevel + 2}`}
                                                        color="text.primary"
                                                    >
                                                        {element.headline}
                                                    </Text>
                                                    {!!element.icon?.name &&
                                                        element.icon.name !== '' && (
                                                            <Flex justifyContent="center">
                                                                <Icon {...element.icon} />
                                                            </Flex>
                                                        )}
                                                    {!!element.text1 && <Box>{element.text1}</Box>}
                                                    {!!element.text2 && <Box>{element.text2}</Box>}
                                                </Flex>
                                            )}
                                        </Table.Cell>
                                    )
                                })}
                            </Table.Row>
                        </React.Fragment>
                    ))}

                    {/* Row: CTAS */}
                    <Table.Row>
                        {selectedItems.map((itemIndex, columnIndex) => {
                            const item = products[itemIndex]
                            return (
                                <Table.Cell
                                    key={`cta-${columnIndex}`}
                                    data-testid={`column-${columnIndex}-ctas`}
                                    {...styles.element}
                                    paddingTop={{base: 'spacing-24', s: 'spacing-32'}}
                                    paddingBottom="spacing-32"
                                    display={
                                        columnIndex >= 2
                                            ? {base: 'none', s: 'table-cell'}
                                            : 'table-cell'
                                    }
                                >
                                    <Flex
                                        flexDirection="column"
                                        alignItems="center"
                                        gap="spacing-16"
                                    >
                                        {/*Primary CTA*/}
                                        {item.primaryCta?.label && (
                                            <CTA
                                                {...item.primaryCta}
                                                variant="secondaryGhost"
                                                trackingContext={{
                                                    moduleName: moduleType,
                                                    eventLabel: 'primaryCta'
                                                }}
                                            />
                                        )}
                                        {/*Secondary CTA*/}
                                        {item.secondaryCta?.label && (
                                            <CTA
                                                {...item.secondaryCta}
                                                variant="text"
                                                trackingContext={{
                                                    moduleName: moduleType,
                                                    eventLabel: 'secondaryCta'
                                                }}
                                            />
                                        )}
                                    </Flex>
                                </Table.Cell>
                            )
                        })}
                    </Table.Row>
                </Table.Body>
            </Table.Root>
        </ModuleContainer>
    )
})

if (process.env.NODE_ENV !== 'production') {
    ComparisonTable.displayName = 'ComparisonTable'
}

ComparisonTable.propTypes = {
    moduleId: PropTypes.string,
    moduleType: PropTypes.string,
    pageId: PropTypes.string,
    headingLevel: PropTypes.number,
    spacing: PropTypes.oneOf(['default', 'reduced', 'min', 'min-top', 'min-bottom']),
    anchor: PropTypes.string,
    background: PropTypes.oneOf(['default', 'subtle', 'strong', 'inverse']),
    heading: PropTypes.object,
    products: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string,
            image: PropTypes.object,
            comparisonProduct: PropTypes.shape({
                productId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                label: PropTypes.string,
                uspProduct: PropTypes.shape({
                    masterProductId: PropTypes.string,
                    value: PropTypes.string
                }),
                colorProduct: PropTypes.shape({
                    masterProductId: PropTypes.string,
                    colors: PropTypes.arrayOf(
                        PropTypes.shape({
                            colorCode: PropTypes.string,
                            swatch: PropTypes.string,
                            name: PropTypes.string,
                            alt: PropTypes.string
                        })
                    )
                }),
                priceProduct: PropTypes.shape({
                    productId: PropTypes.string,
                    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                    valueListPrice: PropTypes.number,
                    currency: PropTypes.string,
                    label: PropTypes.string
                })
            }),
            attributes: PropTypes.arrayOf(
                PropTypes.shape({
                    headline: PropTypes.string,
                    text1: PropTypes.string,
                    text2: PropTypes.string,
                    icon: PropTypes.object
                })
            ),
            primaryCta: PropTypes.object,
            secondaryCta: PropTypes.object
        })
    )
}

export default ComparisonTable
